import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  createIncident,
  deleteIncident,
  fetchCentres,
  searchApartmentParents,
  fetchComplexes,
  fetchIncidents,
  resolveIncident,
  updateIncident
} from 'api/apartment'

// The generic CRUD list still lives under the apartment module; it is not
// apartment-specific, it just has not been moved yet.
import ApartmentApiCrudPage from '../Apartment/shared/ApartmentApiCrudPage'

const TYPES = [
  'Child injury or safety incident',
  'Parent complaint',
  'Teacher misconduct',
  'Property damage',
  'Other'
]

const CENTRE = 'centre'
const APARTMENT = 'apartment'

const columns = [
  { name: 'incident_date', label: 'Date', options: { filter: false } },
  {
    name: 'location',
    label: 'Location',
    options: {
      filter: false,
      customBodyRender: (v) => (v ? v.name : null)
    }
  },
  { name: 'incident_type', label: 'Type' },
  { name: 'child_name', label: 'Child', options: { filter: false } },
  { name: 'description', label: 'Summary', options: { filter: false } },
  {
    name: 'status',
    label: 'Status',
    options: {
      customBodyRender: (v) => (
        <Chip
          size="small"
          label={v}
          color={v === 'Open' ? 'secondary' : 'default'}
        />
      )
    }
  },
  {
    name: 'escalated',
    label: 'Escalated',
    options: { customBodyRender: (v) => (v ? 'Yes' : '') }
  }
]

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(3, 3, 0)
  },
  field: { minWidth: 200 }
}))

const Issues = () => {
  const classes = useStyles()
  const [kind, setKind] = React.useState(CENTRE)
  const [places, setPlaces] = React.useState([])
  const [placeId, setPlaceId] = React.useState('')

  // Reload the place list whenever the kind flips, and select the first one.
  React.useEffect(() => {
    let active = true
    const load = kind === CENTRE ? fetchCentres : fetchComplexes
    load()
      .then((rows) => {
        if (!active) return
        // Centres are keyed by uuid, apartments by slug — that is what the
        // backend filter and the create payload each expect.
        const mapped = rows.map((r) => ({
          id: kind === CENTRE ? r.uuid : r.slug,
          name: r.name
        }))
        setPlaces(mapped)
        setPlaceId(mapped.length ? mapped[0].id : '')
      })
      .catch(() => active && setPlaces([]))
    return () => {
      active = false
    }
  }, [kind])

  // A child is always chosen through their parent — never from a flat list of
  // every child in the building. Centres have no apartment-scoped parent list,
  // so there the child field falls back to free text.
  const [children, setChildren] = React.useState([])
  React.useEffect(() => {
    setChildren([])
  }, [kind, placeId])

  const fetchParents = React.useCallback(
    (text) => searchApartmentParents(placeId, text),
    [placeId]
  )

  const fields = [
    { name: 'incident_type', label: 'Type', type: 'select', options: TYPES },
    { name: 'incident_date', label: 'Date', type: 'date' },
    ...(kind === APARTMENT
      ? [
          {
            name: 'parent',
            label: 'Parent (search by name or number)',
            type: 'autocomplete',
            fetchValues: fetchParents,
            onSelect: (opt) => setChildren(opt?.children || [])
          },
          {
            name: 'child_name',
            label: 'Child (if applicable)',
            type: 'select',
            options: [
              { value: '', label: '— Not child-specific —' },
              ...children.map((c) => ({ value: c.name, label: c.name }))
            ]
          }
        ]
      : [{ name: 'child_name', label: 'Child (if applicable)', type: 'text' }]),
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'action_taken', label: 'Immediate Action Taken', type: 'textarea' }
  ]

  const fetchList = React.useCallback(() => {
    if (!placeId) return Promise.resolve([])
    return fetchIncidents(
      kind === CENTRE ? { centre: placeId } : { apartment: placeId }
    )
  }, [kind, placeId])

  // `parent` is a UI-only field (it scopes the child dropdown); the incident
  // endpoint does not accept it.
  const formToPayload = ({ parent, ...values }) => values

  const createItem = React.useCallback(
    (payload) =>
      createIncident({
        ...payload,
        ...(kind === CENTRE
          ? { centreUuid: placeId }
          : { apartmentSlug: placeId })
      }),
    [kind, placeId]
  )

  const resolve = async (row, reload) => {
    // eslint-disable-next-line no-alert
    const notes = window.prompt('Resolution notes', '') || ''
    try {
      await resolveIncident(row.uuid, {
        action: 'resolve',
        resolution_notes: notes
      })
      reload()
    } catch (error) {
      // request.js toasts
    }
  }
  const escalate = async (row, reload) => {
    try {
      await resolveIncident(row.uuid, { action: 'escalate' })
      reload()
    } catch (error) {
      // request.js toasts
    }
  }

  const rowActions = (row, reload) =>
    row.status === 'Open' ? (
      <>
        <Button
          size="small"
          color="primary"
          onClick={() => resolve(row, reload)}
        >
          Resolve
        </Button>
        {!row.escalated && (
          <Button size="small" onClick={() => escalate(row, reload)}>
            Escalate
          </Button>
        )}
      </>
    ) : null

  return (
    <>
      <div className={classes.bar}>
        <FormControl className={classes.field}>
          <InputLabel>Location type</InputLabel>
          <Select value={kind} onChange={(e) => setKind(e.target.value)}>
            <MenuItem value={CENTRE}>Centre</MenuItem>
            <MenuItem value={APARTMENT}>Apartment</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.field}>
          <InputLabel>{kind === CENTRE ? 'Centre' : 'Apartment'}</InputLabel>
          <Select value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
            {places.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <ApartmentApiCrudPage
        title="Issues & Notes"
        tableTitle="Issues & Notes"
        entityLabel="Issue"
        showSelector={false}
        fetchList={fetchList}
        createItem={createItem}
        formToPayload={formToPayload}
        updateItem={updateIncident}
        deleteItem={deleteIncident}
        columns={columns}
        fields={fields}
        refreshKey={`${kind}:${placeId}`}
        rowActions={rowActions}
        emptyValues={{
          incident_type: 'Other',
          incident_date: '',
          parent: '',
          child_name: '',
          description: '',
          action_taken: ''
        }}
      />
    </>
  )
}

export default Issues
